import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { KibButtonNew } from '@chewy/kib-controls-react'
import KibDivider from '@chewy/kib-divider-react'
import { KibAddIcon, KibInfoOutlineIcon } from '@chewy/kib-icons-react'
import { KibSkeletonText } from '@chewy/kib-skeleton-react'
import { KibTooltip, KibTooltipTrigger } from '@chewy/kib-tooltip-react'
import { PermissionArea } from '@pbt/pbt-ui-components'
import { LoveList as LoveListIcon } from '@pbt/pbt-ui-components/src/icons'

import { getClientPreferencesIsLoading } from '~/store/duck/clientPreferences'
import { getCRUDByArea } from '~/store/reducers/auth'
import type {
  ClientPreferencesDisplayData,
  PatientPreferencesDisplayData,
} from '~/types'

import { PreferencesDisplayFormSection } from './PreferencesDisplayFormSection'
import RhapPreferencesDisplayRow from './RhapPreferencesDisplayRow'

import '@chewy/kib-skeleton-styles/dist/styles/kib-skeleton-text.css'
import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import '@chewy/kib-tooltip-styles/dist/kib-tooltip.css'
import '@chewy/kib-divider-styles/dist/kib-divider.css'
import styles from './RhapPreferencesDisplayWidget.module.scss'

export interface RhapPreferencesDisplayWidgetProps {
  headerText: string
  onEditPreferences?: () => void
  preferences?: ClientPreferencesDisplayData | PatientPreferencesDisplayData
  showInfoIcon?: boolean
}

const RhapPreferencesDisplayWidget = ({
  headerText,
  preferences = {},
  showInfoIcon = false,
  onEditPreferences,
}: RhapPreferencesDisplayWidgetProps) => {
  const { t } = useTranslation(['Clients', 'Common'])
  const buttonRef = useRef(null)
  const clientPreferencesIsLoading = useSelector(getClientPreferencesIsLoading)
  const { read: patientReadPermissions, update: patientUpdatePermissions } =
    useSelector(getCRUDByArea(PermissionArea.PATIENT))

  const categories = Object.keys(preferences).filter(
    (category) => preferences[category as keyof typeof preferences],
  )
  const hasPreferences = categories.length > 0

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <p>{headerText}</p>
        {showInfoIcon && (
          <>
            <KibTooltipTrigger
              className={styles.infoIconButton}
              data-testid="info-icon-trigger"
              ref={buttonRef}
            >
              <KibInfoOutlineIcon />
            </KibTooltipTrigger>
            <KibTooltip className={styles.infoTooltip} trigger={buttonRef}>
              <div className={styles.infoTooltipContent}>
                <span>{t('Clients:CLIENT_PREFERENCES.DISPLAY.TOOLTIP')}</span>
                <span className={styles.loveListIcon}>
                  <LoveListIcon />
                </span>
              </div>
            </KibTooltip>
          </>
        )}
        {hasPreferences && patientUpdatePermissions && onEditPreferences && (
          <KibButtonNew
            className={styles.editButton}
            emphasis="tertiary"
            size="small"
            theme="utility"
            onClick={onEditPreferences}
          >
            {t('Common:EDIT_ACTION')}
          </KibButtonNew>
        )}
      </div>
      <KibDivider className={styles.divider} />
      <div className={styles.body}>
        {!hasPreferences && onEditPreferences && patientUpdatePermissions && (
          <div className={styles.addButtonContainer}>
            <KibButtonNew
              emphasis="tertiary"
              icon={<KibAddIcon />}
              size="small"
              theme="utility"
              onClick={onEditPreferences}
            >
              {t('Clients:PATIENT_SECTION.BUTTON.ADD_PREFERENCES')}
            </KibButtonNew>
          </div>
        )}
        {clientPreferencesIsLoading ? (
          <div
            className={styles.skeletonsContainer}
            data-testid="skeletons-text-container"
          >
            <KibSkeletonText lines={3} short={false} />
          </div>
        ) : (
          <>
            {hasPreferences && patientReadPermissions && (
              <>
                {categories.map((category) => {
                  if (category === 'forms') {
                    return (
                      <PreferencesDisplayFormSection
                        key={category}
                        preferences={
                          preferences[category as keyof typeof preferences]
                        }
                      />
                    )
                  }
                  const { categoryTitle, subCategory } =
                    preferences[category as keyof typeof preferences] || {}
                  return (
                    <RhapPreferencesDisplayRow
                      categoryTitle={categoryTitle || ''}
                      key={categoryTitle}
                      subCategory={subCategory || []}
                    />
                  )
                })}
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default RhapPreferencesDisplayWidget
